import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, CreditCard, FlaskConical, Landmark, Loader2, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DebitQuoteSummary } from "@/components/customer/PlanCheckoutDialog";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtMoney } from "@/lib/format";

type Method = "debito" | "tarjeta" | "demo";

// Contratar un plan: el cliente revisa el plan y elige cómo pagarlo (débito automático de Armony,
// tarjeta con Stripe o, en entornos de prueba, un pago de demostración que no cobra nada).
export default function ContratarPlan() {
  usePageTitle("Contratar plan");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const planId = params.get("plan") || "";
  const interval = params.get("modalidad") === "year" ? "year" : "month";
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState("");
  const [quote, setQuote] = useState<any>(null);
  const [method, setMethod] = useState<Method | "">("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/plans").then((r) => setData(r.data)).catch((e) => setFailed(apiError(e)));
  }, []);

  const plan = data?.plans?.find((p: any) => p.id === planId && !p.free);
  const price = plan?.prices?.[interval];
  const methods = data?.payment_methods || {};

  useEffect(() => {
    if (!data || !price) return;
    // Por defecto: débito si el cliente puede usarlo; si no, tarjeta.
    setMethod((m) => m || (methods.debito ? "debito" : "tarjeta"));
    if (methods.debito) {
      api.get("/payments/debit/quote", { params: { lookup_key: price.lookup_key } }).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }
  }, [data, price?.lookup_key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (failed) return <p className="text-sm text-red-700" role="alert">{failed}</p>;
  if (!data) return <div className="flex justify-center h-96 items-center" role="status"><Loader2 className="w-7 h-7 text-gold animate-spin" aria-hidden="true" /><span className="sr-only">Cargando…</span></div>;
  // Con un plan vigente se cambia desde Mis planes (no se abre otra contratación).
  const status = data.current?.subscription_status;
  if (!plan || !price || status === "active" || status === "past_due" || data.debit?.pending_request) {
    return <Navigate to="/portal/planes" replace />;
  }

  const confirm = async () => {
    setBusy(true);
    try {
      if (method === "debito") {
        const { data: r } = await api.post("/payments/debit/subscribe", { lookup_key: price.lookup_key });
        toast.success(r.active ? `¡Listo! Ya tienes ${r.plan_name}.` : "Recibimos tu solicitud. Tu plan se activará cuando Armony confirme el cobro.");
        await refresh();
        navigate("/portal/planes", { replace: true });
        return;
      }
      const { data: r } = await api.post("/payments/checkout", { lookup_key: price.lookup_key, origin_url: window.location.origin, method });
      // Stripe Checkout (otro sitio) o la página de pago simulado/de demostración, que es de esta misma app.
      const url = new URL(r.checkout_url, window.location.origin);
      if (url.origin === window.location.origin || url.pathname === "/pago-simulado") navigate(url.pathname + url.search);
      else window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const per = interval === "year" ? "al año" : "al mes";
  const options: { key: Method; title: string; sub: string; icon: React.ComponentType<any>; disabled?: string }[] = [
    { key: "debito", title: "Débito automático Armony", icon: Landmark,
      sub: "Se suma al débito que ya tienes con Armony. Sin tarjeta.",
      disabled: methods.debito ? undefined : "Disponible para clientes de Armony con débito automático activo y al día." },
    { key: "tarjeta", title: "Tarjeta de crédito o débito", icon: CreditCard,
      sub: data.stripe_simulated ? "Con Stripe. En este entorno el pago con tarjeta es simulado: no se cobra nada." : "Pago seguro con Stripe. Puedes cancelar cuando quieras." },
    ...(methods.demo ? [{ key: "demo" as Method, title: "Pago de demostración", icon: FlaskConical,
      sub: "Solo para pruebas: activa el plan sin cobrar nada ni pedir una tarjeta." }] : []),
  ];

  return (
    <div className="max-w-4xl space-y-6 animate-fade-in" data-testid="contratar-plan">
      <Link to="/portal/planes" className="inline-flex items-center gap-1 text-sm text-navy hover:underline">
        <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Volver a Mis planes
      </Link>
      <div>
        <p className="text-sm text-slate-500">Contratar plan</p>
        <h1 className="text-3xl font-serif font-medium">{plan.name} · {interval === "year" ? "Anual" : "Mensual"}</h1>
      </div>

      <div className="grid lg:grid-cols-5 gap-6 items-start">
        <section className="lg:col-span-2 card-obsidian p-6" aria-labelledby="plan-summary-title">
          <h2 id="plan-summary-title" className="text-sm font-semibold text-slate-800">Tu plan</h2>
          <p className="mt-3 flex items-baseline gap-1">
            <span className="text-4xl font-bold text-navy" data-testid="contratar-precio">{fmtMoney(price.amount)}</span>
            <span className="text-sm text-slate-600">{per}</span>
          </p>
          <p className="text-xs text-slate-600 mt-1">{interval === "year" ? "Un solo pago al año" : "Se renueva cada mes"}</p>
          {plan.includes_note && <p className="mt-5 text-xs font-semibold text-navy uppercase tracking-wide">{plan.includes_note}</p>}
          <ul className="mt-3 space-y-2">
            {plan.features.map((f: string) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" aria-hidden="true" />{f}</li>
            ))}
          </ul>
        </section>

        <section className="lg:col-span-3 card-obsidian p-6 space-y-5" aria-labelledby="pay-method-title">
          <h2 id="pay-method-title" className="text-lg font-semibold">¿Cómo quieres pagar?</h2>
          <fieldset className="space-y-3">
            <legend className="sr-only">Forma de pago</legend>
            {options.map((o) => (
              <label key={o.key} className={`flex items-start gap-3 p-4 rounded-xl border text-sm ${o.disabled ? "opacity-60 cursor-not-allowed border-slate-200" : "cursor-pointer"} ${method === o.key ? "border-navy bg-navy/5 ring-1 ring-navy" : !o.disabled ? "border-slate-200 hover:bg-slate-50" : ""}`}>
                <input type="radio" name="metodo" value={o.key} checked={method === o.key} disabled={!!o.disabled} onChange={() => setMethod(o.key)}
                  className="mt-1 accent-navy" data-testid={`pay-method-${o.key}`} />
                <o.icon className="w-5 h-5 text-navy shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  <span className="block font-semibold text-slate-900">{o.title}</span>
                  <span className="block text-slate-600">{o.disabled || o.sub}</span>
                </span>
              </label>
            ))}
          </fieldset>

          {method === "debito" && (quote ? <DebitQuoteSummary q={quote} /> : <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-navy" aria-hidden="true" /></div>)}
          {method === "tarjeta" && (
            <p className="text-sm text-slate-700 flex items-start gap-2" data-testid="pay-summary-tarjeta">
              <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-emerald-700" aria-hidden="true" />
              Te llevaremos a la página de pago {data.stripe_simulated ? "simulado" : "segura de Stripe"}. Tu plan se activa en cuanto se confirme el pago: {fmtMoney(price.amount)} {per}.
            </p>
          )}
          {method === "demo" && (
            <p className="text-sm text-amber-900 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40" data-testid="pay-summary-demo">
              Verás una página de pago de demostración. El plan se activa como si el pago se hubiera hecho, sin cobrar nada.
            </p>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => navigate("/portal/planes")} disabled={busy} className="rounded-full border-slate-200 text-slate-700">Cancelar</Button>
            <Button onClick={confirm} disabled={busy || !method || (method === "debito" && !quote)} data-testid="contratar-confirm" className="rounded-full gold-gradient-bg text-white font-semibold px-6">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Procesando" />
                : method === "debito" ? "Confirmar con débito automático" : method === "demo" ? "Continuar al pago de demostración" : "Continuar al pago con tarjeta"}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
